import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Search from "../../components/form/Search";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import Pagination from "../../components/Pagination";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { Role, useAuthStore } from "../../stores/auth";
import { useDeliveryNoteTemplatesStore } from "../../stores/delivery_note_templates";

function TemplateRow({ template, onDelete }) {
  const { t } = useTranslation();

  return (
    <tr>
      <td>
        <Link to={`${template.id}/edit`} className="fw-bold">
          {template.name}
        </Link>
      </td>
      <td className="text-center">{template.items?.length || 0}</td>
      <td>{template.created_by?.name || "-"}</td>
      <td>{template.updated_by?.name || "-"}</td>
      <td className="text-end">
        <div className="btn-group">
          <Link to={`${template.id}/edit`} className="btn btn-sm btn-primary">
            <i className="fa fa-pen" />
          </Link>
          {onDelete && (
            <button className="btn btn-sm btn-danger" onClick={() => onDelete(template)}>
              <i className="fa fa-trash" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function DeliveryNoteTemplates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useSearchParams();

  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const canView = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);
  const canDelete = hasRole(Role.ADMINISTRATOR);

  const [byIds, allIds, total, fetchSome, deleteOne] = useDeliveryNoteTemplatesStore(
    (state) => [state.byIds, state.allIds, state.total, state.fetchSome, state.deleteOne],
    shallow,
  );

  const templates = allIds.map((id) => byIds[id]);

  const search = queryParams.get("q") || "";
  const defaultLimit = useResponsiveLimit(25);
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const page = parseInt(queryParams.get("page")) || 0;
  const offset = page * limit;

  useEffect(() => {
    fetchSome({ q: search, limit, offset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit]);

  const handleSearch = (e) => {
    setQueryParams(`page=${0}&q=${e.target.value}`);
  };

  const [templateToDelete, setTemplateToDelete] = useState(null);

  const handleDelete = (template) => {
    setTemplateToDelete(template);
  };

  const confirmDelete = async () => {
    await deleteOne({ id: templateToDelete.id });
    setTemplateToDelete(null);
  };

  if (!canView) {
    navigate("/");
    return null;
  }

  return (
    <div>
      <Breadcrumb items={[{ label: t("delivery_notes.templates.templates") }]}>
        <Link to="/delivery_note_templates/add" className="btn btn-red">
          <i className="fa fa-plus fa-lg me-2 ms-n2"></i> {t("menu.add")}
        </Link>
      </Breadcrumb>

      <div className="card border-0">
        <div className="card-body p-3">
          <Search handleSearch={handleSearch} search={search} />

          <div className="table-responsive mb-3">
            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("delivery_notes.templates.name")}</th>
                  <th width={1} className="text-center">
                    {t("delivery_notes.templates.item_count")}
                  </th>
                  <th>{t("delivery_notes.templates.created_by")}</th>
                  <th>{t("common.updated_by")}</th>
                  <th width={1} className="text-end">
                    {t("menu.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <TemplateRow key={template.id} template={template} onDelete={canDelete ? handleDelete : null} />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            basePath="/delivery_note_templates"
            page={page}
            limit={limit}
            total={total}
            queryParams={queryParams}
            label={t("delivery_notes.templates.templates")}
          />
        </div>
      </div>

      {templateToDelete && (
        <ConfirmDialog
          title={t("common.remove")}
          message={`${templateToDelete.name} ${t("common.remove")}?`}
          confirmLabel={t("common.remove")}
          confirmClassName="btn btn-danger"
          onCancel={() => setTemplateToDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

export default DeliveryNoteTemplates;
